import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { subscriptionAPI } from '../services/api';

interface TierInfo {
  code: string;
  name: string;
  price: number;
}

interface SubscriptionData {
  subscription_id: string;
  status: string;
  tier: TierInfo;
  current_period_end: string;
  days_remaining: number;
  is_owner: boolean;
  payment_method: string;
}

interface UsageInfo {
  display_name: string;
  limit: number;
  current: number;
  remaining: number;
  percentage: number;
  unlimited: boolean;
}

interface MemberInfo {
  member_id: string;
  student_id: string;
  student_name: string;
  student_phone: string;
  is_owner: boolean;
  joined_at: string;
}

interface SubscriptionContextType {
  hasSubscription: boolean;
  subscription: SubscriptionData | null;
  usage: Record<string, UsageInfo>;
  members: MemberInfo[];
  canAddMembers: boolean;
  isLoading: boolean;
  error: string | null;
  refreshSubscription: () => Promise<void>;
  addMember: (phoneNumber: string) => Promise<void>;
  removeMember: (memberId: string) => Promise<void>;
  canUseFeature: (featureName: string) => Promise<boolean>;
  getUsagePercentage: (usageKey: string) => number;
  isNearLimit: (usageKey: string) => boolean;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

interface SubscriptionProviderProps {
  children: ReactNode;
}

export const SubscriptionProvider: React.FC<SubscriptionProviderProps> = ({ children }) => {
  const [hasSubscription, setHasSubscription] = useState(false);
  const [subscription, setSubscription] = useState<SubscriptionData | null>(null);
  const [usage, setUsage] = useState<Record<string, UsageInfo>>({});
  const [members, setMembers] = useState<MemberInfo[]>([]);
  const [canAddMembers, setCanAddMembers] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSubscriptionData = async () => {
    // Skip if not authenticated
    const token = localStorage.getItem('access_token');
    if (!token) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const data = await subscriptionAPI.getCurrentSubscription();

      if (data.has_subscription) {
        setHasSubscription(true);
        setSubscription(data.subscription);
        setUsage(data.usage || {});
        setMembers(data.members || []);
        setCanAddMembers(data.can_add_members || false);
      } else {
        setHasSubscription(false);
        setSubscription(null);
        setUsage({});
        setMembers([]);
        setCanAddMembers(false);
      }
    } catch (err: any) {
      console.error('Error fetching subscription:', err);
      setError(err.response?.data?.detail || 'Failed to load subscription');
      setHasSubscription(false);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscriptionData();

    // Auto-refresh every 5 minutes
    const interval = setInterval(fetchSubscriptionData, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  const refreshSubscription = async () => {
    await fetchSubscriptionData();
  };

  const addMember = async (phoneNumber: string) => {
    try {
      setError(null);
      await subscriptionAPI.addMember(phoneNumber);
      await refreshSubscription(); // Reload data
    } catch (err: any) {
      const errorMsg = err.response?.data?.detail || 'Failed to add member';
      setError(errorMsg);
      throw new Error(errorMsg);
    }
  };

  const removeMember = async (memberId: string) => {
    try {
      setError(null);
      await subscriptionAPI.removeMember(memberId);
      await refreshSubscription(); // Reload data
    } catch (err: any) {
      const errorMsg = err.response?.data?.detail || 'Failed to remove member';
      setError(errorMsg);
      throw new Error(errorMsg);
    }
  };

  const canUseFeature = async (featureName: string): Promise<boolean> => {
    try {
      const response = await subscriptionAPI.checkFeatureAccess(featureName);
      return response.has_access || false;
    } catch (err) {
      console.error('Error checking feature access:', err);
      return false;
    }
  };

  const getUsagePercentage = (usageKey: string): number => {
    const usageInfo = usage[usageKey];
    if (!usageInfo) return 0;
    if (usageInfo.unlimited) return 0;
    return usageInfo.percentage;
  };

  const isNearLimit = (usageKey: string): boolean => {
    const percentage = getUsagePercentage(usageKey);
    return percentage >= 80; // 80% or more is "near limit"
  };

  const value: SubscriptionContextType = {
    hasSubscription,
    subscription,
    usage,
    members,
    canAddMembers,
    isLoading,
    error,
    refreshSubscription,
    addMember,
    removeMember,
    canUseFeature,
    getUsagePercentage,
    isNearLimit,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};
